import { postOrderAccept } from '@/api/order-list/postOrderAccept';
import { postOrderComplete } from '@/api/order-list/postOrderComplete';
import { ORDER_STATUS_TYPE } from '@/api/type/commonTypes';
import { OrderItemsType } from '@/api/type/types';
import { Images } from '@/assets';
import SpeechBubble from '@/components/bubble/SpeechBubble';
import { useLoading } from '@/context/LoadingContext';
import RejectReasonModal from '@/pages/order-list/RejectReasonModal';
import { order } from '@/stores/slices/order/orderSlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import RejectedStatus from './RejectedStatus';

interface OrderStatusItemProps {
  item: OrderItemsType;
}

const OrderStatusItem = ({ item }: OrderStatusItemProps) => {
  const [openSelect, setOpenSelect] = useState<boolean>(false);
  const [wrapWidth, setWrapWidth] = useState<number>(0);
  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { startLoading, stopLoading } = useLoading();
  const wrapRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const selectRef = useRef<HTMLDivElement>(null);

  const orderStatusData = (status: ORDER_STATUS_TYPE) => {
    switch (status) {
      case 'PENDING':
        return t('pending2');
      case 'IN_PROGRESS':
        return t('inProgress2');
      case 'REJECTED':
        return t('reject');
      case 'CANCELLED':
        return t('cancel');
      case 'COMPLETED':
        return t('completed2');
    }
  };

  const selectChangeStatus = (status: ORDER_STATUS_TYPE) => {
    switch (status) {
      case 'PENDING':
        return t('pending2');
      case 'IN_PROGRESS':
        return t('inProgress2');
      case 'REJECTED':
        return t('reject');
      case 'COMPLETED':
        return t('completed2');
    }
  };

  const getSelectableStatusList = (
    status: ORDER_STATUS_TYPE
  ): ORDER_STATUS_TYPE[] => {
    switch (status) {
      case 'PENDING':
        return ['IN_PROGRESS', 'REJECTED'];
      case 'IN_PROGRESS':
        return ['COMPLETED'];
      default:
        return []; // COMPLETED, CANCELLED, REJECTED는 선택 불가
    }
  };

  /** 주문 접수 승인 */
  const fetchOrderAccept = useCallback(async () => {
    startLoading();
    try {
      const res = await postOrderAccept({ orderId: item.orderId });
      if (res.isSuccess) {
        dispatch(
          order.updateOrderStatus({
            orderId: item.orderId,
            status: 'IN_PROGRESS',
            history: res.data,
          })
        );
      }
    } finally {
      stopLoading();
    }
  }, [dispatch, item.orderId, startLoading, stopLoading]);

  /** 주문 완료 */
  const fetchOrderComplete = useCallback(async () => {
    startLoading();
    try {
      const res = await postOrderComplete({ orderId: item.orderId });
      if (res.isSuccess) {
        dispatch(
          order.updateOrderStatus({
            orderId: item.orderId,
            status: 'COMPLETED',
            history: res.data,
          })
        );
      }
    } finally {
      stopLoading();
    }
  }, [dispatch, item.orderId, startLoading, stopLoading]);

  const handleSelectStatus = useCallback(
    (newStatus: ORDER_STATUS_TYPE) => async () => {
      if (newStatus === 'IN_PROGRESS') {
        fetchOrderAccept();
      } else if (newStatus === 'REJECTED') {
        setOpenRejectModal(true);
      } else if (newStatus === 'COMPLETED') {
        fetchOrderComplete();
      }
      setOpenSelect(false);
    },
    [fetchOrderAccept, fetchOrderComplete]
  );

  const onCloseRejectModal = useCallback(() => {
    setOpenRejectModal(false);
  }, []);

  useEffect(() => {
    if (wrapRef.current) {
      setWrapWidth(wrapRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        openSelect &&
        selectRef.current &&
        !selectRef.current.contains(e.target as Node)
      ) {
        setOpenSelect(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openSelect]);

  return (
    <>
      {item.status === 'REJECTED' && (
        <RejectedStatus
          status={item.status}
          rejectionReason={item.rejectionReason}
        />
      )}
      {item.status !== 'REJECTED' && (
        <Container ref={wrapRef}>
          <Section>
            <StatusDot $status={item.status} />
            {orderStatusData(item.status)}
          </Section>
          {getSelectableStatusList(item.status).length > 0 ? (
            <SelectWrapper ref={selectRef}>
              <ArrowButton
                onClick={() => setOpenSelect((prev) => !prev)}
                data-selected={openSelect}
              />
              {openSelect && (
                <SelectBox $width={wrapWidth}>
                  {getSelectableStatusList(item.status).map((s) => (
                    <SelectItem key={s} onClick={handleSelectStatus(s)}>
                      <StatusDot $status={s} />
                      {selectChangeStatus(s)}
                    </SelectItem>
                  ))}
                </SelectBox>
              )}
            </SelectWrapper>
          ) : null}
        </Container>
      )}
      <RejectReasonModal
        onClose={onCloseRejectModal}
        open={openRejectModal}
        orderId={item.orderId}
      />
    </>
  );
};

export default OrderStatusItem;

const Container = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  overflow: visible;
  width: 100%;
  justify-content: space-between;
`;

const Section = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StatusDot = styled.div<{ $status: ORDER_STATUS_TYPE }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ $status }) => {
    switch ($status) {
      case 'PENDING':
        return '#FFC400';
      case 'IN_PROGRESS':
        return '#21B837';
      case 'CANCELLED':
        return '#393C3C';
      case 'REJECTED':
        return '#393C3C';
      case 'COMPLETED':
        return '#DBDBDB';
      default:
        return '#E5E5E5';
    }
  }};
  border: ${({ $status }) =>
    $status === 'COMPLETED' ? `1px solid #F2F2F2` : 'none'};
`;

const ArrowButton = styled.button`
  width: 25px;
  height: 22px;
  background-image: url(${Images.StatusArrowImg});
  background-size: cover;
  margin-top: 3px;
  &[data-selected='true'] {
    background-image: url(${Images.SelectedStatusArrowImg});
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  display: flex;
  overflow: visible;
`;

interface SelectBoxProps {
  $width: number;
}

const SelectBox = styled.div<SelectBoxProps>`
  position: absolute;
  top: 100%;
  right: 0px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  z-index: 20;
  min-width: 100px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  width: ${({ $width }) => `calc(${$width}px + 12px)`};
  box-sizing: border-box;
`;

const SelectItem = styled.div`
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  &:hover {
    background-color: #f3f3f3;
  }
`;
